import { useState } from 'react'
import { Copy, ExternalLink, Link2, Zap } from 'lucide-react'
import './App.css'

interface ShortUrlResponse {
  shortCode: string
  shortUrl: string
  originalUrl: string
  expiresAt?: string
}

function App() {
  const [url, setUrl] = useState('')
  const [shortUrl, setShortUrl] = useState<ShortUrlResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)

  const isValidUrl = (string: string): boolean => {
    try {
      new URL(string)
      return true
    } catch (_) {
      return false
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setShortUrl(null)

    if (!url.trim()) {
      setError('请输入URL')
      return
    }

    if (!isValidUrl(url)) {
      setError('请输入有效的URL')
      return
    }

    setLoading(true)

    try {
      const response = await fetch('/api/shorten', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url }),
      })

      if (!response.ok) {
        throw new Error('生成短链接失败')
      }

      const data = await response.json() as ShortUrlResponse
      setShortUrl(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : '生成短链接失败')
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = async () => {
    if (!shortUrl) return

    try {
      await navigator.clipboard.writeText(shortUrl.shortUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      setError('复制失败')
    }
  }

  return (
    <div className="min-h-screen bg-tech-900 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <Link2 className="w-8 h-8 text-primary-400 mr-3" />
            <h1 className="text-4xl font-bold text-white">短链接生成器</h1>
          </div>
          <p className="text-tech-300 text-lg">快速生成短链接，让分享更简单</p>
        </div>

        {/* Main Card */}
        <div className="bg-tech-800 rounded-xl shadow-2xl border border-tech-700 p-8 backdrop-blur-sm">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="url" className="block text-sm font-medium text-tech-200 mb-2">
                输入要缩短的URL
              </label>
              <div className="relative">
                <input
                  type="url"
                  id="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com/very-long-url"
                  className="w-full px-4 py-3 bg-tech-900 border border-tech-600 rounded-lg text-white placeholder-tech-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                  disabled={loading}
                />
                <Link2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-tech-400" />
              </div>
            </div>

            {error && (
              <div className="bg-red-900/20 border border-red-800 text-red-300 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-tech-600 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <Zap className="w-5 h-5 animate-pulse" />
                  <span>生成中...</span>
                </>
              ) : (
                <>
                  <Zap className="w-5 h-5" />
                  <span>生成短链接</span>
                </>
              )}
            </button>
          </form>

          {/* Result */}
          {shortUrl && (
            <div className="mt-8 pt-6 border-t border-tech-700 animate-fade-in">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-tech-200 mb-2">
                    短链接
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={shortUrl.shortUrl}
                      readOnly
                      className="flex-1 px-4 py-3 bg-tech-900 border border-tech-600 rounded-lg text-white font-mono text-sm"
                    />
                    <button
                      onClick={copyToClipboard}
                      className="bg-tech-700 hover:bg-tech-600 text-white px-4 py-3 rounded-lg transition-all duration-200 flex items-center space-x-2"
                    >
                      {copied ? (
                        <span className="text-green-400">已复制!</span>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          <span>复制</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex space-x-4">
                  <a
                    href={shortUrl.shortUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>打开链接</span>
                  </a>
                </div>

                <div className="text-xs text-tech-400">
                  <p>原始URL: {shortUrl.originalUrl}</p>
                  {shortUrl.expiresAt && (
                    <p>过期时间: {new Date(shortUrl.expiresAt).toLocaleString()}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Features */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-tech-800/50 rounded-lg p-6 text-center border border-tech-700">
            <Zap className="w-8 h-8 text-primary-400 mx-auto mb-3" />
            <h3 className="text-white font-semibold mb-2">快速生成</h3>
            <p className="text-tech-300 text-sm">毫秒级响应，即时生成短链接</p>
          </div>
          <div className="bg-tech-800/50 rounded-lg p-6 text-center border border-tech-700">
            <Link2 className="w-8 h-8 text-primary-400 mx-auto mb-3" />
            <h3 className="text-white font-semibold mb-2">永久有效</h3>
            <p className="text-tech-300 text-sm">生成的短链接永久保存</p>
          </div>
          <div className="bg-tech-800/50 rounded-lg p-6 text-center border border-tech-700">
            <Copy className="w-8 h-8 text-primary-400 mx-auto mb-3" />
            <h3 className="text-white font-semibold mb-2">一键复制</h3>
            <p className="text-tech-300 text-sm">便捷复制，方便分享</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default App